import { useRef, type CSSProperties } from 'react';
import { cn } from '@worldcoo/ui/lib/utils';
import { useInView } from '@worldcoo/ui/hooks/use-in-view';

/** One word's fade, and the gap before the next starts: `new-karaoke` in `-styles/theme.css` reads both. */
const WORD_MS = 700;
const STEP_MS = 35;

const splitWords = (text: string) => text.trim().split(/\s+/);

/** When the last word of `text` is fully lit, counted from the moment its sweep starts. */
export function karaokeDurationMs(text: string) {
	return (splitWords(text).length - 1) * STEP_MS + WORD_MS;
}

/**
 * A dimmed paragraph whose words light up one after another once it is on screen, on a clock rather
 * than the scroll. `text` is a string, not `children`, so inline markup is refused rather than mangled.
 */
export function Karaoke(props: {
	text: string;
	className?: string;
	/** Before the first word, once revealed. */
	delayMs?: number;
	/** Off when an ancestor's `data-revealed` should start it, so it keeps time with that ancestor's other parts. */
	observe?: boolean;
}) {
	const ref = useRef<HTMLParagraphElement>(null);
	// Inset, so the sweep is not spent on a line the reader has only just glimpsed. One-way: an ENTRANCE.
	const inView = useInView(ref, { margin: '-64px' });
	const revealed = (props.observe ?? true) && inView;
	// Keyed by word and position, since a sentence repeats words; the list only changes wholesale.
	const words = splitWords(props.text).map((word, index) => ({ word, index, key: `${index}-${word}` }));

	return (
		<p
			ref={ref}
			data-revealed={revealed || undefined}
			className={cn('new-karaoke', props.className)}
			style={
				{
					'--karaoke-word-ms': `${WORD_MS}ms`,
					'--karaoke-step-ms': `${STEP_MS}ms`,
					'--karaoke-delay-ms': `${props.delayMs ?? 0}ms`,
				} as CSSProperties
			}
		>
			{words.map((entry) => (
				<span key={entry.key} className="new-karaoke-word" style={{ '--k-i': entry.index } as CSSProperties}>
					{entry.word}
					{entry.index < words.length - 1 ? ' ' : ''}
				</span>
			))}
		</p>
	);
}
